import React, { useEffect } from "react";
import { useTable, useExpanded } from "react-table";
import { Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import { formatDuration, range } from "../../helpers/stubs";

function ReportsTable() {
  //#region Store Listeners
  const { report } = useSelector((state) => state.orgAdminSlice);
  //#endregion

  //#region Local Helper functions
  const makeData = React.useCallback((data) => {
    const restructuredData = data.map((eachCandidate) => {
      const row = { ...eachCandidate };
      const newData = { ...eachCandidate.candidate };
      newData.name = newData.firstName + newData.lastName;
      newData.tracker = formatDuration(newData.tracker);
      newData.offline = formatDuration(newData.offline);
      newData.totalDuration = formatDuration(newData.totalDuration);
      newData.hourlyRate = "$ " + newData.hourlyRate;
      newData.activity = (
        <div className="text-center">
          <span>{+newData.activity}</span>
          <span style={{ fontSize: "12px" }}>{"% "}</span>
        </div>
      );
      const subRowsData = Object.keys(row.projects).map((item) => {
        return {
          name: item,
          tracker: formatDuration(row.projects[item].tracker),
          offline: formatDuration(row.projects[item].offline),
          totalDuration: formatDuration(row.projects[item].totalDuration),
          activity: (
            <div className="text-center">
              <span>{row.projects[item].activityPercentage}</span>
              <span style={{ fontSize: "12px" }}>{"% "}</span>
            </div>
          ),

          hourlyRate: "$ " + row.projects[item].hourlyRate,
        };
      });
      newData.subRows = subRowsData;
      return newData;
    });
    return restructuredData;
  }, []);
  //#endregion

  //#region Table Data
  const data = React.useMemo(() => makeData(report), [report, makeData]);
  const columns = React.useMemo(
    () => [
      {
        // Build our expander column
        id: "expander", // Make sure it has an ID
        Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) => (
          <span {...getToggleAllRowsExpandedProps()}>
            {isAllRowsExpanded ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                class="bi bi-chevron-down"
                viewBox="0 0 16 16"
              >
                <path
                  fill-rule="evenodd"
                  d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                class="bi bi-chevron-right"
                viewBox="0 0 16 16"
              >
                <path
                  fill-rule="evenodd"
                  d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
                />
              </svg>
            )}
          </span>
        ),
        Cell: ({ row, rows, toggleRowExpanded }) =>
          // Use the row.canExpand and row.getToggleRowExpandedProps prop getter
          // to build the toggle for expanding a row
          row.canExpand ? (
            <span
              {...row.getToggleRowExpandedProps({
                style: {
                  // We can even use the row.depth property
                  // and paddingLeft to indicate the depth
                  // of the row
                },
                onClick: () => {
                  const expandedRow = rows.find((row) => row.isExpanded);

                  if (expandedRow) {
                    const isSubItemOfRow = Boolean(
                      expandedRow && row.id.split(".")[0] === expandedRow.id
                    );

                    if (isSubItemOfRow) {
                      const expandedSubItem = expandedRow.subRows.find(
                        (subRow) => subRow.isExpanded
                      );

                      if (expandedSubItem) {
                        const isClickedOnExpandedSubItem =
                          expandedSubItem.id === row.id;
                        if (!isClickedOnExpandedSubItem) {
                          toggleRowExpanded(expandedSubItem.id, false);
                        }
                      }
                    } else {
                      toggleRowExpanded(expandedRow.id, false);
                    }
                  }
                  row.toggleRowExpanded();
                },
              })}
            >
              {row.isExpanded ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  class="bi bi-chevron-down"
                  viewBox="0 0 16 16"
                >
                  <path
                    fill-rule="evenodd"
                    d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  class="bi bi-chevron-right"
                  viewBox="0 0 16 16"
                >
                  <path
                    fill-rule="evenodd"
                    d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
                  />
                </svg>
              )}
            </span>
          ) : null,
      },
      {
        Header: "candidate",
        accessor: "name", // accessor is the "key" in the data
      },
      {
        Header: "Hours Tracked by Tracker",
        accessor: "tracker",
      },
      {
        Header: "Hours NOT Tracked by Tracker",
        accessor: "offline",
      },
      {
        Header: "Total Hours Spent",
        accessor: "totalDuration", // accessor is the "key" in the data
      },
      {
        Header: "Activity",
        accessor: "activity",
      },
    ],
    []
  );

  const expandAllRows = [...range(0, data.length)].reduce((acc, val, index) => {
    acc[index] = true;
    return acc;
  }, {});

  console.log("expand all rows :- ", expandAllRows);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        initialState: {
          expanded: expandAllRows,
        },
      },
      useExpanded
    );

  return (
    <>
      <Table {...getTableProps()} bordered responsive>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr className="text-center" {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()}>{column.render("Header")}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            console.log("row :- ", row, "index :- ", i);
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  return (
                    <td className="text-center" {...cell.getCellProps()}>
                      {cell.render("Cell")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}

export default ReportsTable;
